import { iBytecodeRepositoryAbi } from "@gearbox-protocol/sdk/abi/310/iBytecodeRepository";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Hex, toBytes, toHex } from "viem";
import {
  useAccount,
  useSignTypedData,
  useSwitchChain,
  useWriteContract,
} from "wagmi";
import { z } from "zod";
import { useWeb3 } from "./use-web3";

const MAX_FILE_SIZE = 1_000_000; // 1MB in bytes

export const bytecodeFileSchema = z.object({
  file: z
    .instanceof(File)
    .refine(file => file.type === "application/json", {
      message: "File must be a JSON file",
    })
    .refine(file => file.name.endsWith(".json"), {
      message: "File must have a .json extension",
    })
    .refine(file => file.size > 0, {
      message: "File must not be empty",
    })
    .refine(file => file.size < MAX_FILE_SIZE, {
      message: "File must be less than 1MB",
    }),
});

// Hex string validation helper - matches strings starting with 0x followed by hex characters
const HexString = z.string().regex(/^0x[0-9a-fA-F]+$/);

// Schema for validating the bytecode content structure
const bytecodeValidationSchema = z.object({
  bytecode: z.object({
    object: HexString,
  }),
});

interface UploadBytecodeParams {
  contractType: string;
  version: number;
  sourceUrl: string;
  jsonFile: File | null;
}

interface UseBytecodeUploadOptions {
  onSuccess?: (bytecodeHash: Hex) => void;
  onError?: (error: Error) => void;
}

export function useBytecodeUpload({
  onSuccess,
}: UseBytecodeUploadOptions = {}) {
  const { switchChainAsync } = useSwitchChain();
  const { signTypedDataAsync } = useSignTypedData();
  const { bytecodeRepository } = useWeb3();
  const { address, chainId } = useAccount();
  const { writeContractAsync } = useWriteContract();

  const mutation = useMutation({
    mutationFn: async ({
      contractType,
      version,
      sourceUrl,
      jsonFile,
    }: UploadBytecodeParams) => {
      if (!jsonFile) {
        throw new Error("No JSON file selected");
      }

      if (!bytecodeRepository) {
        throw new Error("Bytecode repository not found");
      }

      if (!address) {
        throw new Error("Wallet is not connected");
      }

      const file = await bytecodeFileSchema.safeParseAsync({
        file: jsonFile,
      });

      const bytecodeRaw = await file.data!.file.text();

      const validatedBytecode = bytecodeValidationSchema.safeParse(
        JSON.parse(bytecodeRaw),
      );

      if (!validatedBytecode.success) {
        throw new Error(
          `Bytecode validation failed: ${validatedBytecode.error.message}`,
        );
      }

      const { object: bytecode } = validatedBytecode.data.bytecode;

      // Convert contract type to bytes32 and verify its presence in bytecode
      const contractTypeBytes32 = toHex(toBytes(contractType, { size: 32 }));

      if (bytecode.indexOf(contractTypeBytes32.replace("0x", "")) === -1) {
        throw new Error(`Contract type not found in bytecode: ${contractType}`);
      }

      if (chainId != null && chainId !== 1) {
        await switchChainAsync({ chainId: 1 });
      }

      const bytecodeData = {
        contractType: contractTypeBytes32,
        version: BigInt(version),
        initCode: bytecode as Hex,
        author: address,
        source: sourceUrl,
      };

      // Compute bytecode hash and check if it already exists
      const bytecodeHash = bytecodeRepository.computeBytecodeHash(bytecodeData);

      const isBytecodeAlreadyUploaded =
        await bytecodeRepository.isBytecodeUploaded(bytecodeHash);

      if (isBytecodeAlreadyUploaded) {
        throw new Error("Bytecode already uploaded");
      }

      const typedData =
        bytecodeRepository.computeEIP712AuthorDigest(bytecodeData);

      const signature = await signTypedDataAsync(typedData);

      const hash = await writeContractAsync({
        address: bytecodeRepository.address,
        abi: iBytecodeRepositoryAbi,
        functionName: "uploadBytecode",
        args: [
          {
            ...bytecodeData,
            authorSignature: signature,
          },
        ],
      });

      const receipt = await bytecodeRepository.client.waitForTransactionReceipt(
        {
          hash,
          timeout: 120_000,
        },
      );

      if (receipt.status !== "success") {
        throw new Error(`Transaction failed: ${hash}`);
      }

      onSuccess?.(bytecodeHash);
    },
    onSuccess: () => {
      toast.success("Bytecode uploaded successfully");
    },
    onError: error => {
      toast.error("Failed to upload bytecode", {
        description: error.message,
      });
    },
  });

  return {
    uploadBytecode: mutation.mutate,
    isUploading: mutation.isPending,
    error: mutation.error as Error | null,
    isSuccess: mutation.isSuccess,
    reset: mutation.reset,
    /** Address provider + bytecode repo finished loading (avoid submit race). */
    isContractsReady: !!bytecodeRepository,
  };
}
